import React, { useCallback, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DiarySettingsForm } from '@traquility/ui/diary';
import type {
  DiarySettingsFormData,
  DiarySettingsFormContainerProps,
} from '../shared/types/diarySettingsForm';

export type { DiarySettingsFormContainerProps };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10,
    },
  },
});

const UPDATE_DIARY_SETTINGS_MUTATION = `
  mutation UpdateDiarySettings($input: UpdateDiarySettingsInput!) {
    updateDiarySettings(input: $input) {
      diarySetting {
        id
        showAdditionalActivities
        showFoodWidget
      }
      errors
    }
  }
`;

function DiarySettingsFormContainerInner({
  graphqlEndpoint,
  csrfToken,
  initialData,
  backUrl,
  successUrl,
  labels,
  errors: initialErrors = {},
}: DiarySettingsFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async (data: DiarySettingsFormData) => {
    setIsSubmitting(true);
    setErrors({});

    const input = {
      showAdditionalActivities: data.showAdditionalActivities,
      showFoodWidget: data.showFoodWidget,
    };

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: UPDATE_DIARY_SETTINGS_MUTATION, variables: { input } }),
      });

      if (!response.ok) throw new Error('Network error');

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      const mutationResult = result.data.updateDiarySettings;

      if (mutationResult.errors?.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsSubmitting(false);
        return;
      }

      window.location.href = successUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, successUrl]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  return (
    <DiarySettingsForm
      initialData={initialData}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      errors={errors}
      labels={labels}
      isSubmitting={isSubmitting}
    />
  );
}

function DiarySettingsFormContainer(props: DiarySettingsFormContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <DiarySettingsFormContainerInner {...props} />
    </QueryClientProvider>
  );
}

let formRoot: Root | null = null;

export function mountDiarySettingsForm(
  container: HTMLElement,
  props: DiarySettingsFormContainerProps
): Root {
  if (formRoot) formRoot.unmount();
  const root = createRoot(container);
  formRoot = root;
  root.render(<DiarySettingsFormContainer {...props} />);
  return root;
}

export function unmountDiarySettingsForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default DiarySettingsFormContainer;
